import { type JSX, type ReactNode, useEffect, useState } from 'react';
import { enableModalConfirmationAutofocus } from '@gonfalon/dogfood-flags/dogfood-flags';
import { FormGroup } from '@gonfalon/legacy-forms/components';
import { msg } from '@gonfalon/messages/msg';
import {
  Button,
  type ButtonProps,
  Dialog,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalOverlay,
  Text,
  TextField,
} from '@launchpad-ui/components';
// oxlint-disable-next-line no-restricted-imports
import { Checkbox } from '@launchpad-ui/core';
import classNames from 'clsx';
import { isFunction } from 'lodash';

import { Logger } from '#utils/logUtils';

import './styles.css';

const logger = Logger.get('Confirmation');

export type ConfirmationProps = {
  onConfirm?: (data?: FormData & number) => void;
  onCancel?: () => void;
  title: string;
  description?: string | JSX.Element;
  asyncBody?:
    | string
    | JSX.Element
    | (({ onReady, focusRef }: { onReady?: () => void; focusRef?: React.Ref<HTMLElement> }) => JSX.Element);
  body?: string | JSX.Element | (() => JSX.Element);
  form?: React.ElementType;
  formProps?: {};
  confirmLabel: string;
  abortLabel?: string;
  validation?: { placeholder?: string; onValidate: (input: string) => boolean; label: ReactNode } | null;
  testId?: string;
  kind?: ButtonProps['variant'];
  canConfirm?: boolean;
  acknowledgementText?: string;
  className?: string;
  copyableValue?: string;
  isPending?: boolean;
};

export const Confirmation = ({
  abortLabel = 'Cancel',
  kind = 'default',
  validation = null,
  canConfirm = true,
  acknowledgementText,
  isPending,
  ...props
}: ConfirmationProps) => {
  const isAsyncBody = () => !!props.asyncBody;

  const [isBodyReady, setIsBodyReady] = useState(!isAsyncBody());
  const [isTestValid, setIsTestValid] = useState<boolean | undefined>(!validation);
  const [isFormValid, setIsFormValid] = useState(!props.form);
  const [acknowledged, setAcknowledged] = useState(!acknowledgementText);
  const [formValue, setFormValue] = useState<FormData & number>();

  useEffect(() => {
    setAcknowledged(!acknowledgementText);
  }, [acknowledgementText]);

  const handleTestChange = (value: string) => {
    setIsTestValid(validation?.onValidate(value || ''));
  };

  const handleFormChange = (value: FormData & number, isValid: boolean) => {
    setFormValue(value);
    setIsFormValid(isValid);
  };

  const handleBodyReady = () => {
    setIsBodyReady(true);
  };

  const handleConfirmClick = () => {
    if (!canMakeChange()) {
      return;
    }
    confirm();
  };

  const canMakeChange = () => isTestValid && isFormValid && canConfirm;

  const isReady = () => isBodyReady && isTestValid && isFormValid && canConfirm && acknowledged;

  const parseBodyProp = () => {
    const { asyncBody, body } = props;

    if (asyncBody && body) {
      logger.warn('Only one of asyncBody or body should be specified on Confirmation');
    }

    return asyncBody || body;
  };

  const confirm = () => {
    props.onConfirm?.(formValue);
  };

  const { onCancel, className, description } = props;
  const { title, form: Form, confirmLabel, testId } = props;
  const classes = classNames('Confirmation', `Confirmation--${kind}`, className);
  const disabled = !isReady();
  const shouldAutoFocusConfirm = enableModalConfirmationAutofocus() && !validation && !acknowledgementText && !Form;

  const readyProp = isAsyncBody() && handleBodyReady;
  const bodyProp = parseBodyProp();
  let bodyContent;

  if (isFunction(bodyProp)) {
    const BodyComponent = bodyProp;
    bodyContent = <BodyComponent onReady={readyProp || undefined} />;
  } else {
    bodyContent = <div>{bodyProp}</div>;
  }

  const handleOpenChange = (isOpen: boolean) => {
    // Swallow close requests while the confirmed action is in flight (see the
    // dismissal-blocking note on <ModalOverlay> below) so no stray dismissal
    // path can fire onCancel out from under the running operation.
    if (!isOpen && !isPending) {
      onCancel?.();
    }
  };

  return (
    // While the confirmed action is pending, every dismissal path is blocked —
    // a backdrop click (isDismissable), escape (isKeyboardDismissDisabled), and
    // the close and cancel buttons below: closing the dialog cannot stop the
    // in-flight operation, so allowing it would let the dialog disappear while
    // the action still completes behind the user.
    <ModalOverlay
      defaultOpen
      onOpenChange={handleOpenChange}
      isDismissable={!isPending}
      isKeyboardDismissDisabled={isPending}
    >
      <Modal className={classes} size="small">
        <Dialog>
          <div slot="header">
            <Heading slot="title">{title}</Heading>
            <IconButton
              aria-label="close"
              icon="cancel"
              size="small"
              variant="minimal"
              onPress={onCancel}
              isDisabled={isPending}
            />
            {description && <Text slot="subtitle">{description}</Text>}
          </div>
          <div slot="body">
            <div>
              {bodyContent}
              {Form && <Form {...props.formProps} onChange={handleFormChange} />}
              {isBodyReady && validation && (
                <div className="Confirmation-validation">
                  <TextField
                    id="confirmation-validation-text-field"
                    aria-label={msg('ui.confirmation.confirmLabel')}
                    onChange={handleTestChange}
                    autoComplete="off"
                  >
                    <div className="Confirmation-validationLabel">{validation.label}</div>
                    <Input placeholder={validation.placeholder} data-test-id="confirmation-validation-text-field" />
                  </TextField>
                </div>
              )}
              {acknowledgementText && (
                <FormGroup className="Confirmation-conflict">
                  <Checkbox
                    className="Conflict-acknowledgement"
                    value={acknowledged.toString()}
                    checked={acknowledged}
                    onChange={(event) => {
                      setAcknowledged(event.target.checked);
                    }}
                  >
                    {acknowledgementText}
                  </Checkbox>
                </FormGroup>
              )}
            </div>
          </div>
          <div slot="footer">
            <Button onPress={onCancel} isDisabled={isPending}>
              {abortLabel}
            </Button>
            <Button
              onPress={handleConfirmClick}
              variant={kind === 'default' ? 'primary' : kind}
              isDisabled={disabled || isPending}
              isPending={isPending}
              autoFocus={shouldAutoFocusConfirm && !disabled && !isPending}
              data-test-id={testId || 'confirm-button'}
              name={confirmLabel}
            >
              {confirmLabel}
            </Button>
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
